import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { useParams } from 'react-router-dom';
import { has, isEqual } from 'lodash';

import { useSnackBar } from 'context/SnackBarContext';
import Dialog from 'components/ui/Dialog';
import Form from 'components/ui/Form';
import {
  InputAdornment,
  InputLabel,
  OutlinedInput,
  FormControl,
} from '@material-ui/core';
import {
  FormSectionTitle,
  GridContainer,
  StyledField,
  StyledHelperText,
} from 'styles/form';
import Select from 'components/ui/Select';
import { createStaff, deleteStaff, showStaff, updateStaff } from 'api/staff';
import AddressFormFields from 'components/general/AddressFormFields';
import GeneralInfoFormFields from 'components/general/GeneralInfoFormFields';
import { staffType } from 'options/staff-type.options';
import { revertPhoneMask } from 'helpers/revert-phone-mask';
import { initialValues, validationSchema } from '../staff.config';

const Staff = ({
  modalOpen,
  handleModalClose,
  blockRouteChange,
  setBlockRouteChange,
}) => {
  const { id } = useParams();

  const [staff, setStaff] = useState();
  const { setMessage, setOpenSnackBar, setSeverity } = useSnackBar();

  useEffect(async () => {
    if (id) {
      const { data } = await showStaff(id);
      setStaff(data.data);
    }
  }, []);

  const handleSave = async (values) => {
    if (staff) {
      try {
        await updateStaff(id, values);
        setMessage('Saved changes');
        setSeverity('success');
      } catch (error) {
        console.error(error);
        setMessage('Something went wrong. Failed to save changes.');
        setSeverity('error');
      }
    } else {
      try {
        await createStaff(values);
        setMessage('Staff created');
        setSeverity('success');
      } catch (error) {
        console.error(error);
        setMessage('Something went wrong. Failed to create staff.');
        setSeverity('error');
      }
    }

    setOpenSnackBar(true);
    handleModalClose(true);
  };

  const handleDelete = async () => {
    try {
      await deleteStaff(id);
      setMessage('Staff deleted');
      setSeverity('success');
    } catch (error) {
      console.error(error);
      setMessage('Something went wrong. Failed to delete staff.');
      setSeverity('error');
    }

    setOpenSnackBar(true);
    handleModalClose(true);
  };

  return (
    <Formik
      initialValues={initialValues(staff)}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={handleSave}
    >
      {({
        values,
        errors,
        setFieldValue,
        handleChange,
        submitForm,
        touched,
      }) => {
        const {
          firstName,
          lastName,
          email,
          phone,
          address,
          city,
          state,
          postalCode,
          country,
          type,
          rate,
          mileageRate,
        } = values;

        const initValues = initialValues(staff);

        const unsavedChange = !isEqual(
          {
            ...values,
            mileageRate: Number(mileageRate),
            phone: revertPhoneMask(phone),
          },
          { ...initValues, mileageRate: Number(initValues.mileageRate) },
        );

        if (unsavedChange) {
          setBlockRouteChange(true);
        } else {
          setBlockRouteChange(false);
        }

        return (
          <Dialog
            open={modalOpen}
            handleClose={handleModalClose}
            submitForm={submitForm}
            typeText="Staff"
            handleDelete={handleDelete}
            blockRouteChange={blockRouteChange}
          >
            <FormSectionTitle>Staff</FormSectionTitle>
            <Form fullWidth onSubmit={submitForm}>
              <GridContainer>
                <GeneralInfoFormFields
                  firstName={firstName}
                  lastName={lastName}
                  email={email}
                  phone={phone}
                  errors={errors}
                  touched={touched}
                  handleChange={handleChange}
                />

                <AddressFormFields
                  address={address}
                  country={country}
                  city={city}
                  state={state}
                  postalCode={postalCode}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  handleChange={handleChange}
                  fieldsRequired
                />

                <Select
                  options={staffType}
                  label="Type"
                  variant="outlined"
                  errors={touched.type && has(errors, 'type')}
                  touched={touched}
                  helperText={touched.type && errors.type}
                  name="type"
                  onChange={handleChange}
                  value={type}
                  disableNone
                  required
                />

                <FormControl fullWidth variant="outlined">
                  <StyledField variant="outlined">
                    <InputLabel
                      htmlFor="outlined-adornment-amount"
                      error={touched.rate && has(errors, 'rate')}
                      required
                    >
                      Rate ($/hr)
                    </InputLabel>
                    <OutlinedInput
                      startAdornment={
                        <InputAdornment position="start">$</InputAdornment>
                      }
                      labelWidth={85}
                      error={touched.rate && has(errors, 'rate')}
                      helperText={touched.rate && errors.rate}
                      name="rate"
                      onChange={handleChange}
                      value={rate}
                      required
                    />
                  </StyledField>
                  {errors.rate && touched.rate && (
                    <StyledHelperText error={has(errors, 'rate')} bottom="-1">
                      field is required
                    </StyledHelperText>
                  )}
                </FormControl>

                <FormControl fullWidth variant="outlined">
                  <StyledField variant="outlined">
                    <InputLabel
                      htmlFor="outlined-adornment-amount"
                      error={touched.mileageRate && has(errors, 'mileageRate')}
                    >
                      Mileage Rate ($/km)
                    </InputLabel>
                    <OutlinedInput
                      startAdornment={
                        <InputAdornment position="start">$</InputAdornment>
                      }
                      labelWidth={150}
                      error={touched.mileageRate && has(errors, 'mileageRate')}
                      helperText={touched.mileageRate && errors.mileageRate}
                      name="mileageRate"
                      onChange={handleChange}
                      value={mileageRate}
                    />
                  </StyledField>
                  {errors.mileageRate && touched.mileageRate && (
                    <StyledHelperText
                      error={has(errors, 'mileageRate')}
                      bottom="-1"
                    >
                      field is required
                    </StyledHelperText>
                  )}
                </FormControl>
              </GridContainer>
            </Form>
          </Dialog>
        );
      }}
    </Formik>
  );
};

export default Staff;
